import {useEffect, useState, useMemo, useCallback} from "react";
import api from "../api";
import {getStoreStatus, isOpenAt as isOpenAtPure, formatTime} from "../Data/storeSchedule";

/**
 * useStoreOpen — reactive store-open status.
 *
 * Combines two layers:
 *   1. Server `/api/online` flag (runtime kill switch from /admin)
 *   2. Static `storeSchedule.js` (compiled-in hours + manual override)
 *
 * If EITHER says we're closed, we're closed.
 *
 * Returns { isOpen, reason, closesAt, nextOpen, isOpenAt(date) }.
 * Re-evaluates every minute so the banner can flip when the store
 * crosses an open/close boundary while the page is visible.
 */
export default function useStoreOpen() {
  const [now, setNow] = useState(() => new Date());
  const [serverOnline, setServerOnline] = useState(true); // optimistic until we hear otherwise

  useEffect(() => {
    let cancelled = false;
    const fetchOnline = async () => {
      try {
        const res = await api.get("/api/online");
        // server returns boolean true/false (or "true"/"false" string)
        if (!cancelled) setServerOnline(res.data !== false && res.data !== "false");
      } catch {
        // if the endpoint fails, default to optimistic: don't lock people out
        // because the server is having a hiccup.
      }
    };
    fetchOnline();
    // refetch every 2 minutes so admin toggles propagate without a refresh
    const id = setInterval(fetchOnline, 120_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    // Re-check once per minute. We don't need second-level precision —
    // open/close boundaries are minute-aligned.
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const status = useMemo(() => {
    if (!serverOnline) {
      return {
        isOpen: false,
        reason: "We're not taking online orders right now.",
        closesAt: null,
        nextOpen: null,
      };
    }
    return getStoreStatus(now);
  }, [now, serverOnline]);

  const isOpenAt = useCallback((date) => isOpenAtPure(date), []);

  return {
    ...status,                                   // isOpen, reason, closesAt, nextOpen
    closesAtFormatted: status.closesAt ? formatTime(status.closesAt) : null,
    nextOpenFormatted: status.nextOpen
      ? formatNextOpen(status.nextOpen, now)
      : null,
    isOpenAt,
  };
}

/**
 * "Wednesday at 11:00 AM" or "tomorrow at 11:00 AM" or "today at 5:00 PM"
 */
function formatNextOpen(nextOpen, now) {
  const sameDay = isSameDay(nextOpen, now);
  const tomorrow = isSameDay(nextOpen, addDays(now, 1));
  const time = formatTime(`${pad(nextOpen.getHours())}:${pad(nextOpen.getMinutes())}`);
  if (sameDay) return `today at ${time}`;
  if (tomorrow) return `tomorrow at ${time}`;
  const dayName = nextOpen.toLocaleDateString("en-US", {weekday: "long"});
  return `${dayName} at ${time}`;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function pad(n) { return String(n).padStart(2, "0"); }
